import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { hiresLineToAddress, toHex } from "../../../common/utility"
import { useGlobalContext } from "../../globalcontext"
import { nColsHgrMagnifier, nRowsHgrMagnifier } from "../../graphics"
import { handleGetHeatMapGetMem, handleGetHeatMapGetMemMax, handleGetHeatMapSetMem, handleGetHeatMapSetMemMax, handleGetMemoryDump } from "../../main2worker"
import { PaletteName } from "viridis"
import { getPreferenceByString } from "../../localstorage"
import { getHeatMapRGBValues, getViridisColorsRGB, linearizeColor } from "../../ui_utilities"

type MemoryTableProps = {
  addressGetTable: Uint32Array | null
  isHGR: boolean
  offset: number
  highAscii: boolean
  highlight: number[]
  scrollRow: number
  pickWatchpoint: boolean
  heatGetMem: boolean
  doPickWatchpoint: (addr: number) => void
  doSetMemory: (address: number, value: number) => void
}

// Above this many rows we stop materializing the whole table and render only the rows
// inside the scroll window. The HGR view is exactly 192 rows and its cells double as the
// magnifier's selection target, so it is deliberately left alone.
const VIRTUALIZE_ABOVE_ROWS = 192
// Rows rendered beyond each edge of the window, so a single arrow-key step does not
// immediately run off the end of the rendered rows.
const OVERSCAN_ROWS = 4
// Only used until a real row has been measured, which happens before the first paint,
// so the error can never be seen.
const ASSUMED_ROW_HEIGHT = 16

const MemoryTable = (props: MemoryTableProps) => {
  const { hgrMagnifierLoc: hgrMagnifierLoc, setHgrMagnifierLoc: setHgrMagnifierLoc,
    setUpdateHgrMagnifier: setUpdateHgrMagnifier } = useGlobalContext()
  const hgrMagnifierLocal = useRef([-1, -1])
  const cellValue = useRef("")
  const tableRef = useRef<HTMLTableElement>(null)
  // Set when keyboard navigation targets a row that is not currently rendered.
  const pendingFocus = useRef<[number, number] | null>(null)
  const [scrollTop, setScrollTop] = useState(0)
  // Measured from a real row; stays 0 until the first layout pass.
  const [rowHeightPx, setRowHeightPx] = useState(0)
  const rowHeight = rowHeightPx > 0 ? rowHeightPx : ASSUMED_ROW_HEIGHT
  const isLandscape = (window.innerWidth > window.innerHeight)
  const height = isLandscape ? Math.max((window.innerHeight - 647), 100) : 250

  const clearSelection = (table: HTMLTableElement) => {
    if (!table || !table.rows) return
    for (let i = 0; i < table.rows.length; i++) {
      for (let j = 0; j < table.rows[i].cells.length; j++) {
        table.rows[i].cells[j].classList.remove("selected")
      }
    }
  }

  const setSelection = (offset: number[], table: HTMLTableElement) => {
    for (let j = offset[1] + 1; j <= offset[1] + nRowsHgrMagnifier; j++) {
      for (let i = offset[0] + 1; i <= offset[0] + nColsHgrMagnifier; i++) {
        const cell = table?.rows[j]?.cells[i]
        cell?.classList.add("selected")
      }
    }
  }

  // Make sure we keep our selection up to date, especially if it was changed
  // by clicking on the canvas.
  useEffect(() => {

    // // This scrolling code is used with the HGR mode, where we want to scroll
    // // both vertically and horizontally to keep the HGR selection box in view.
    // const scrollHgrIntoView = (table: HTMLTableElement) => {
    //   const cell1 = table.rows[hgrMagnifierLoc[1] + 1].cells[hgrMagnifierLoc[0] + 1]
    //   const cell2 = table.rows[hgrMagnifierLoc[1] + nRowsHgrMagnifier].cells[hgrMagnifierLoc[0] + 2]
    //   if (cell1 && cell2) {
    //     const r1 = cell1.getBoundingClientRect()
    //     const r2 = cell2.getBoundingClientRect()
    //     const tr = table.getBoundingClientRect()
    //     const f = 10
    //     const isInView = (r1.left - 4 * f) >= tr.left && (r1.top - f) >= tr.top &&
    //       (r2.right + f) <= tr.right && (r2.bottom + f) <= tr.bottom
    //     if (!isInView) {
    //       cell1.scrollIntoView({ block: "center", inline: "center" })
    //     }
    //   }
    // }

    if (props.isHGR) {
      if ((hgrMagnifierLoc[0] >= 0) &&
        (hgrMagnifierLocal.current[0] !== hgrMagnifierLoc[0] ||
          hgrMagnifierLocal.current[1] !== hgrMagnifierLoc[1])) {
        hgrMagnifierLocal.current[0] = hgrMagnifierLoc[0]
        hgrMagnifierLocal.current[1] = hgrMagnifierLoc[1]
        setTimeout(() => {
          const table = document.querySelector("#memory-table") as HTMLTableElement
          if (!table) return
          clearSelection(table)
          setSelection(hgrMagnifierLoc, table)
          // scrollHgrIntoView(table)
        }, 10)
      }
    } else {
      setTimeout(() => {
        const table = document.querySelector("#memory-table") as HTMLTableElement
        clearSelection(table)
      }, 10)
    }
  }, [hgrMagnifierLoc, props.isHGR])

  const convertByteToAscii = (byte: number) => {
    if (props.highAscii) byte &= 0x7F
    if (byte < 32 || byte > 126) return "·" // Non-printable
    return String.fromCharCode(byte)
  }

  const width = props.isHGR ? 40 : 16
  const memory = handleGetMemoryDump()
  const nrows = memory.length <= 1 ? 0 : (props.isHGR ? 192 : Math.floor(memory.length / width))

  const makeRow = (row: number) => {
    const addr = props.isHGR ?
      (hiresLineToAddress(props.offset, row) - props.offset) : 16 * row
    const mem = memory.slice(addr, addr + width)
    const cells = [toHex(props.isHGR ? addr + props.offset : addr, 4) + ":"]
    let ascii = ""
    for (let b = 0; b < width; b++) {
      cells.push(toHex(mem[b]))
      if (!props.isHGR) ascii += convertByteToAscii(mem[b])
    }
    if (!props.isHGR) {
      cells.push(ascii)
    }
    return cells
  }

  const getMemoryOffset = (cell: HTMLTableCellElement): [number, number] => {
    const row = cell.parentNode
    const table = cell.parentNode?.parentNode as HTMLTableElement
    if (table && table.rows && row && row.parentNode) {
      const nrows = table.rows.length
      const ncols = table.rows[0].cells.length
      // The header row doesn't count as a real row, so just find the row index.
      const rawRow = Array.from(row.parentNode.children).indexOf(row as Element)
      // However, for the column, subtract 1 to get rid of the address column.
      const rawCol = Array.from(row.children).indexOf(cell) - 1
      let cellIndex = -1
      let rowIndex = -1
      if (props.isHGR) {
        cellIndex = Math.min(rawCol, 40 - nColsHgrMagnifier)
        rowIndex = Math.min(rawRow, nrows - nRowsHgrMagnifier)
      } else {
        cellIndex = Math.min(rawCol, ncols - 3)
        rowIndex = Math.min(rawRow, nrows - 8)
      }
      if (rowIndex >= 0 && cellIndex >= 0) {
        return [cellIndex, rowIndex]
      }
    }
    return [-1, -1]
  }

  const onMouseDown = (e: React.MouseEvent) => {
    const cell = e.target as HTMLTableCellElement
    const offset = getMemoryOffset(cell)
    if (offset[0] < 0) return
    if (props.pickWatchpoint) {
      const addr = props.isHGR ?
        (hiresLineToAddress(props.offset, offset[1]) + offset[0]) :
        (16 * offset[1] + offset[0] + props.offset)
      props.doPickWatchpoint(addr)
      setTimeout(() => {
        cell.style.animation = ""
      }, 2250)
      // If we were picking a watchpoint, do not go into edit mode.
      e.preventDefault()
      return
    }
    if (props.isHGR) {
      setHgrMagnifierLoc(offset)
      setUpdateHgrMagnifier(true)
    }
  }

  const onMouseOver = (e: React.MouseEvent) => {
    if (e.buttons === 1) {
      onMouseDown(e)
    }
  }

  const setNewFocus = (table: HTMLTableElement, col: number, row: number) => {
    const nextCell = table.rows[row]?.cells[col]
    if (nextCell) {
      nextCell.focus()
      return
    }
    // The target row is outside the rendered window. Scroll it in; it turns into a
    // real row on the next render, and the layout effect below focuses it then.
    pendingFocus.current = [col, row]
    table.scrollTop = Math.max(0, (row + 0.5) * rowHeight - table.clientHeight / 2)
  }

  const handleKeyDown = (col: number, row: number, e: React.KeyboardEvent<HTMLDivElement>) => {
    // Use arrow keys to move from cell to cell
    const cell = e.currentTarget
    const table = cell.parentNode?.parentNode as HTMLTableElement
    const nrows = table.rows.length
    const lastCol = props.isHGR ? 40 : 16
    if (e.key.startsWith("Arrow")) {
      e.preventDefault()
      cellValue.current = ""
      if (e.key === "ArrowUp") {
        if (row < 1) return
        row--
      } else if (e.key === "ArrowDown") {
        if (row >= (nrows - 1)) return
        row++
      } else if (e.key === "ArrowLeft") {
        if (col > 1) {
          col--
        } else if (row >= 1) {
          row--
          col = lastCol
        } else {
          return
        }
      } else if (e.key === "ArrowRight") {
        if (col <= (lastCol - 1)) {
          col++
        } else if (row < (nrows - 1)) {
          row++
          col = 1
        } else {
          return
        }
      }
      setNewFocus(table, col, row)
    } else if (e.key === "Escape") {
      e.preventDefault()
      const cell = e.currentTarget
      // On an Escape, restore the original value
      if (cell && cellValue.current) {
        cell.textContent = cellValue.current
      }
    } else if (e.key === "Enter") {
      e.preventDefault()
      const cell = e.currentTarget as HTMLTableCellElement
      if (cell && cell.textContent?.trim() === "") {
        // TODO: Check spreadsheet programs to see if advance to next cell on 'Enter'
        cellValue.current = "00"
        setNewValue(col, row, cell, "00")
      }
      if (col < (lastCol - 1)) {
        col++
        setNewFocus(table, col, row)
      }
    }
  }

  // When table cell comes into focus, select the text in it.
  const handleFocus = (cell: HTMLTableCellElement) => {
    const range = document.createRange()
    const sel = window.getSelection()
    range.selectNodeContents(cell)
    // Remember current value in case user hits Escape
    if (cell && cell.textContent) {
      cellValue.current = cell.textContent
    }
    sel?.removeAllRanges()
    sel?.addRange(range)
  }

  const setNewValue = (col: number, row: number, cell: HTMLTableCellElement, newvalue: string) => {
    const addr = (props.isHGR ?
      hiresLineToAddress(props.offset, row) : (16 * row + props.offset)) + col - 1
    const value = parseInt(newvalue, 16)
    props.doSetMemory(addr, value)
    cell.textContent = newvalue
    cellValue.current = ""
  }

  const handleInput = (col: number, row: number, cell: HTMLTableCellElement) => {
    const newText = cell.textContent
    if (!newText) {
      return
    }
    const newvalue = newText.replace(/[^0-9a-f]/gi, "").toUpperCase().substring(0, 2)
    cell.textContent = newvalue
    if (newvalue.length === 1) {
      // If we needed to replace the contents (e.g. to make uppercase),
      // the cursor will unfortunately be placed at the beginning of the cell.
      // To fix this, force the cursor to the end.
      const range = document.createRange()
      range.selectNodeContents(cell)
      range.collapse(false) // false means collapse to the end
      const selection = window.getSelection()
      if (selection) {
        selection.removeAllRanges()
        selection.addRange(range)
      }
    } else if (newvalue.length === 2) {
      // We're done editing. Set the new memory value and advance to next cell.
      setNewValue(col, row, cell, newvalue)
      // Advance the selection to the next cell
      const table = cell.parentNode?.parentNode as HTMLTableElement
      const nrows = table.rows.length
      const lastCol = props.isHGR ? 40 : 16
      if (col <= (lastCol - 1)) {
        col++
      } else if (row < (nrows - 1)) {
        row++
        col = 1
      }
      setNewFocus(table, col, row)
    }
  }

  // Make sure we keep our selection up to date, especially if it was changed
  // by clicking on the canvas.
  // (useEffect moved above early return to satisfy rules-of-hooks)

  const isEditable = (col: number, row: number) => {
    if (col === 0 || col === (width + 1)) return false
    if (!props.addressGetTable) return true
    const index = props.addressGetTable[Math.floor(row / width)]
    return (index < 0x10000) || (index >= 0x17F00)
  }

  // const applyHighlightAnimation = (element: HTMLElement) => {
  //   const isDarkMode = getTheme() == UI_THEME.DARK
  //   const animationName = isDarkMode ? "highlight-anim-dark" : "highlight-anim"
  //   element.style.animation = `${animationName} 3s 0.5s`
  // }

  // This scrolling code is used by the higher-level MemoryDump component to
  // scroll to a specific row when the address field is changed.
  if (props.scrollRow >= 0) {
    const table = document.querySelector("#memory-table") as HTMLTableElement
    const row = table.rows[props.scrollRow + 1]
    if (row) {
      row.scrollIntoView({ block: "center", inline: "nearest" })
     // applyHighlightAnimation(row)
      // Tried to also highlight the address column, but it does strange things
      // in HGR mode where it draws some of the columns on top of each other...
      //    applyHighlightAnimation(row.cells[0])
      setTimeout(() => {
        row.style.animation = ""
        // row.cells[0].style.animation = ''
      }, 3500)
    }
  }

  const cellClass = (col: number, row: number) => {
    if (col === 0) return "memtable-addr"
    if (col === 17) return ""
    const highlight = (props.highlight.includes(row * width + col - 1)) ? " memtable-highlight" : ""
    return (isEditable(col, row) ? "" : "memtable-readonly") + highlight
  }

  const heatMap = (props.heatGetMem) ? handleGetHeatMapGetMem() : handleGetHeatMapSetMem()
  const heatMapMax = (props.heatGetMem) ? handleGetHeatMapGetMemMax().value : handleGetHeatMapSetMemMax().value
  const heatMapPalette = getPreferenceByString("heatMapColorTable", "Spectral") as PaletteName
  const colorTable = getViridisColorsRGB(heatMapPalette, 16)

  const getCellStyle = (col: number, row: number) => {
    if (col < 0 || col >= 16) return {}
    const heatMapValue = heatMap[row * width + col] || 0
    if (heatMapValue === 0) return {}
    // Don't stomp on top of a currently highlighted cell
    if ((props.highlight.includes(row * width + col)) ) return {}
    const [r, g, b, a] = getHeatMapRGBValues(heatMapValue, heatMapMax, colorTable)
    const alpha = (heatMapValue !== 0) ? a : 0
    const luminance = (heatMapValue !== 0) ? 0.2126 * linearizeColor(r) + 0.7152 * linearizeColor(g) +
      0.0722 * linearizeColor(b) : 1
    const textColor = luminance > 0.179 ? "black" : "white"
    const style = {backgroundColor: `rgba(${r}, ${g}, ${b}, ${alpha})`,
      color: textColor}
    return style
  }

  // Materialize only the rows inside the scroll window; every other row becomes an empty
  // placeholder <tr>, one per logical row. Keeping one <tr> per row (rather than just
  // collapsing the gap) means table.rows[row] still refers to the same line, which is what
  // the keyboard navigation, the magnifier selection and the scroll-to-row code all
  // assume, and the scrollbar keeps its true range because a placeholder takes its height
  // from exactly the same CSS as a real row.
  const virtualize = nrows > VIRTUALIZE_ABOVE_ROWS
  const windowStart = virtualize
    ? Math.max(0, Math.floor(scrollTop / rowHeight) - OVERSCAN_ROWS) : 0
  const windowEnd = virtualize
    ? Math.min(nrows, windowStart + Math.ceil(height / rowHeight) + 2 * OVERSCAN_ROWS)
    : nrows
  const rows: Array<Array<string> | null> = []
  for (let row = 0; row < nrows; row++) {
    rows.push((row >= windowStart && row < windowEnd) ? makeRow(row) : null)
  }

  // Height comes from the real row that was measured, so a placeholder is exactly as tall
  // as the row it stands in for whatever the cell CSS does. padding/border are zeroed so
  // the specified height is the whole box, with no box-sizing ambiguity. One shared object
  // for every placeholder keeps React's diff cheap.
  const placeholderStyle = useMemo(() => ({
    height: `${rowHeight}px`, padding: 0, border: 0,
  }), [rowHeight])

  // The <table> is its own scroll container (display: block + overflow: auto), so that
  // is where the scroll position lives.
  useEffect(() => {
    const table = tableRef.current
    if (!table) return
    const onScroll = () => setScrollTop(table.scrollTop)
    table.addEventListener("scroll", onScroll, { passive: true })
    return () => table.removeEventListener("scroll", onScroll)
  }, [])

  // Placeholders already render at the right height, so this measurement is only needed
  // to turn scrollTop into a row index. It runs before paint and only stores a changed
  // value, so it settles after one pass instead of looping.
  useLayoutEffect(() => {
    if (!virtualize) return
    const row = tableRef.current?.rows[windowStart + 1]
    if (!row || row.cells.length < 2) return
    const measured = Math.round(row.getBoundingClientRect().height * 10) / 10
    if (measured > 0 && measured !== rowHeightPx) setRowHeightPx(measured)
  }, [virtualize, windowStart, rowHeightPx])

  // Navigation can aim at a row that is currently a placeholder. setNewFocus scrolls it
  // into the window and parks the target here; once that scroll has re-rendered with the
  // row materialized, the cell finally exists and can take focus.
  useLayoutEffect(() => {
    const pending = pendingFocus.current
    if (!pending) return
    const cell = tableRef.current?.rows[pending[1] + 1]?.cells[pending[0]]
    if (!cell) return
    pendingFocus.current = null
    cell.focus()
  }, [scrollTop, windowStart])

  return (
    <table className="memtable" id="memory-table" ref={tableRef}
      style={{ cursor: props.pickWatchpoint ? "crosshair" : "default",
        lineHeight: "10pt", height: `${height}px` }}
      onMouseDown={onMouseDown}
      onMouseOver={onMouseOver}>
      <thead>
        <tr>
          <th style={{ position: "sticky", top: "0", left: "0", zIndex: "2" }}></th>
          {Array.from({ length: width }, (_, i) => <th className="memtable-hex" key={i}>{toHex(i, 2)}</th>)}
          {props.isHGR ? null : <th className="memtable-ascii" key="ascii">ASCII</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, j) => (
          row === null
            ? <tr key={j}><td colSpan={props.isHGR ? width + 1 : width + 2} style={placeholderStyle} /></tr>
            : <tr key={j}>
              {row.map((cell, i) => (
                <td key={i}
                  contentEditable={isEditable(i, j)}
                  suppressContentEditableWarning={true}
                  onKeyDown={(e) => handleKeyDown(i, j, e)}
                  onInput={(e) => handleInput(i, j, e.currentTarget)}
                  onFocus={(e) => handleFocus(e.currentTarget)}
                  className={cellClass(i, j)}
                  style={getCellStyle(i - 1, j)}>
                  {cell}
                </td>
              ))}
            </tr>
        ))}
      </tbody>
    </table>
  )
}

export default MemoryTable
